import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { TabContent, TabPane } from "reactstrap";
import { useDispatch, connect } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import { Oval as Loader } from "react-loader-spinner";

// Assets and styles
import "./profilePage.css";

// Core components
import { getLocalItem } from "../../../../shared/utils/config/localStorage";
import { isUserLoggedIn, getUserRole, getUserId } from "../../../../shared/utils/function";
import NoContent from "../../../component/NoContent/NoContent";
import ModernHeader from "../../../../components/layout/Headers/ModernHeader";
import ModernFootermenu from "../../../../components/layout/Footer";

// Actions and utilities
import { TAB_TYPE } from "../../../../shared/constants/common";
import { fetchContentByCreator, fetchContentCounts } from "../../../../shared/actions/contentAction";
import { fetchCreator } from "../../../../shared/actions/creatorActions";

// Sections and Modals
import UserProfileHeader from "../../../component/profile/header";
import FeedPost from "../../../../features/cards/creator/feed";
import MediaGrid from "../../../../features/cards/creator/mediaGrid";
import { reportError } from "../../../../shared/sentry";

const ProfilePageCreator = ({
  user,
  creator,
  creatorLoading = false,
}) => {
  const dispatch = useDispatch();
  const isLoggedIn = isUserLoggedIn();
  const userRole = getUserRole();
  const userId = getUserId();

  // State hooks
  const [activeTab, setActiveTab] = useState(TAB_TYPE.FEED);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [type, setType] = useState(TAB_TYPE.FEED);
  const [filter, setFilterState] = useState("");
  const [contentCounts, setContentCounts] = useState({});
  const [feedLists, setFeedLists] = useState([]);
  const [mediaLists, setMediaLists] = useState([]);
  const [freeContentLists, setFreeContentLists] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isPinAlreadyExists, setIsPinAlreadyExists] = useState(false);

  // Dynamic limit based on tab type - feed: 6, media & free: 12
  const limit = type === TAB_TYPE.FEED ? 6 : 12;
  const hasCreatorData = Boolean(creator?.user_data?.username);

  // Track loading state and pagination per tab type
  const [tabState, setTabState] = useState({
    feed: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 },
    media: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 },
    free: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 }
  });



  useEffect(() => {
    document.body.classList.add('pt-0')
  }, [])

  const setitems = useCallback((res, isLoadMore = false) => {
    // Check if response has error or invalid structure
    if (!res.success || res.error || !res.data) {
      setLoading(false);
      setLoadingMore(false);
      setTabState(prev => ({
        ...prev,
        [type]: { ...prev[type], hasInitiallyLoaded: true, hasMoreData: false }
      }));
      return;
    }

    // Extract content - it might be directly in res.data or in res.data.content
    const newContent = Array.isArray(res.data) ? res.data : (res.data.content || []);
    // If we received fewer items than the limit, there's no more data
    const hasMore = newContent.length === limit;

    if (type === TAB_TYPE.FEED) {
      if (isLoadMore) {
        setFeedLists(prev => [...prev, ...newContent]);
        setIsPinAlreadyExists(newContent.some(p => p.pin === true));
      } else {
        setFeedLists(newContent);
        setIsPinAlreadyExists(newContent.some(p => p.pin === true));
      }
    } else if (type === TAB_TYPE.MEDIA) {
      if (isLoadMore) {
        setMediaLists(prev => [...prev, ...newContent]);
      } else {
        setMediaLists(newContent);
      }
    } else if (type === TAB_TYPE.FREE) {
      if (isLoadMore) {
        setFreeContentLists(prev => [...prev, ...newContent]);
      } else {
        setFreeContentLists(newContent);
      }
    }

    setLoading(false);
    setLoadingMore(false);
    setTabState(prev => ({
      ...prev,
      [type]: { ...prev[type], hasInitiallyLoaded: true, hasMoreData: hasMore }
    }));
  }, [type, limit]);

  const fetchContentCountsData = useCallback(async (contentCreatorId) => {
    try {
      if (!contentCreatorId) {
        // If contentCreatorId is not available yet, wait for it
        return;
      }

      const result = await fetchContentCounts(contentCreatorId);
      if (result.success && result.data) {
        setContentCounts(result.data);
      } else {
        setContentCounts({});
      }
    } catch (err) {
      setContentCounts({});
    }
  }, []);

  // for fetch content 
  const fetchContent = useCallback((currentPage, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    const filterValue = filter || undefined;
    dispatch(fetchContentByCreator(
      true,
      userId,
      type,
      limit,
      currentPage,
      filterValue,
      (res) => {
        setitems(res, isLoadMore);
      }
    ));
  }, [dispatch, userId, type, limit, filter, setitems]);

  const loadInitialContent = useCallback(() => {
    setTabState(prev => ({
      ...prev,
      [type]: { ...prev[type], page: 0, hasMoreData: true }
    }));
    fetchContent(0, false);
  }, [type, fetchContent]);

  const handleSetType = useCallback((newType) => {
    setType(newType);
    setFilterState("");
    // Set loading state immediately if tab hasn't been loaded yet
    if (!tabState[newType].hasInitiallyLoaded) {
      setLoading(true);
    }
  }, [tabState]);

  const loadMore = useCallback(() => {
    // Check if there's more data available and not currently loading
    const currentTabState = tabState[type];
    if (!currentTabState.hasMoreData || loadingMore || loading) {
      return;
    }

    const nextPage = currentTabState.page + 1;
    setTabState(prev => ({
      ...prev,
      [type]: { ...prev[type], page: nextPage }
    }));
    fetchContent(nextPage, true);
  }, [tabState, type, loadingMore, loading, fetchContent]);

  // Fetch content counts once on mount
  useEffect(() => {
    if (userId) {
      fetchContentCountsData(userId);
    }
  }, [userId, fetchContentCountsData]);

  // Load feed only after creator header data is ready — avoids spinner + skeleton flashing together
  useEffect(() => {
    if (!hasCreatorData || !type || tabState[type].hasInitiallyLoaded) {
      return;
    }
    loadInitialContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, hasCreatorData]);

  // Ref for the sentinel element (load more trigger)
  const loadMoreSentinelRef = useRef(null);

  // Setup Intersection Observer for load more (more reliable across browsers)
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return;

    // Use Intersection Observer API for better cross-browser compatibility
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        // When sentinel element is visible (or near visible), load more
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null, // viewport
        rootMargin: '200px', // Trigger 200px before reaching the sentinel
        threshold: 0.1 // Trigger when 10% of sentinel is visible
      }
    );

    observer.observe(sentinel);

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [loadMore, type]); // Re-initialize when tab type changes

  const refreshContent = useCallback(() => {
    // Reset the current tab state
    setTabState(prev => ({
      ...prev,
      [type]: { hasInitiallyLoaded: false, hasMoreData: true, page: 0 }
    }));
    fetchContent(0, false);

    // Refresh content counts
    if (userId) {
      fetchContentCountsData(userId);
    }
  }, [type, fetchContent, userId, fetchContentCountsData]);

  // Handle new content upload - add to appropriate tabs
  const handleNewContentUpload = useCallback(async () => {
    try {
      // Refetch creator so header free post count (freePictures) and other stats stay in sync
      const creatorUsername = creator?.user_data?.username;
      if (creatorUsername) {
        dispatch(fetchCreator(creatorUsername));
      }

      // Refresh content counts first to get updated counts
      if (userId) {
        await fetchContentCountsData(userId);
      }

      // Refresh all tabs that have been loaded to include new content
      const tabsToRefresh = [];
      if (tabState.feed.hasInitiallyLoaded) tabsToRefresh.push(TAB_TYPE.FEED);
      if (tabState.media.hasInitiallyLoaded) tabsToRefresh.push(TAB_TYPE.MEDIA);
      if (tabState.free.hasInitiallyLoaded) tabsToRefresh.push(TAB_TYPE.FREE);

      // Refresh each loaded tab
      for (const tabType of tabsToRefresh) {
        const tabLimit = tabType === TAB_TYPE.FEED ? 6 : 12;
        const filterValue = tabType === TAB_TYPE.FEED ? filter : undefined;

        dispatch(fetchContentByCreator(
          true,
          userId,
          tabType,
          tabLimit,
          0,
          filterValue,
          (res) => {
            if (res?.success && res?.data) {
              const newContent = Array.isArray(res.data) ? res.data : (res.data.content || []);

              if (tabType === TAB_TYPE.FEED) {
                setFeedLists(newContent);
                setIsPinAlreadyExists(newContent.some(p => p.pin === true));
              } else if (tabType === TAB_TYPE.MEDIA) {
                setMediaLists(newContent);
              } else if (tabType === TAB_TYPE.FREE) {
                setFreeContentLists(newContent);
              }
            }
          }
        ));
      }

      // If current tab hasn't been loaded yet, load it
      if (!tabState[type].hasInitiallyLoaded) {
        loadInitialContent();
      }
    } catch (error) {
      reportError(error, 'Creator Profile refresh after upload');
      // Fallback to simple refresh
      refreshContent();
    }
  }, [userId, tabState, type, filter, dispatch, fetchContentCountsData, loadInitialContent, refreshContent, creator]);

  // Handle dropdown toggle - only one dropdown open at a time
  const handleDropdownToggle = useCallback((postId) => {
    setOpenDropdownId(prev => prev === postId ? null : postId);
  }, []);

  // Handle post deletion
  const createPostDeleteHandler = useCallback((listType) => {
    return (id) => {
      setFeedLists(prev => prev.filter(p => (p._id || p.id) !== id));
      setMediaLists(prev => prev.filter(p => (p._id || p.id) !== id));
      setFreeContentLists(prev => prev.filter(p => (p._id || p.id) !== id));
      if (userId) fetchContentCountsData(userId);
    };
  }, [userId, fetchContentCountsData]);

  const handleFeedPostDeleted = useMemo(() => createPostDeleteHandler(TAB_TYPE.FEED), [createPostDeleteHandler]);
  const handleMediaPostDeleted = useMemo(() => createPostDeleteHandler(TAB_TYPE.MEDIA), [createPostDeleteHandler]);
  const handleFreePostDeleted = useMemo(() => createPostDeleteHandler(TAB_TYPE.FREE), [createPostDeleteHandler]);

  // Handle post updates (price, pin, unpin, lock, unlock) - consolidated handler
  const createPostUpdateHandler = useCallback((listType) => {
    return (action, postId, newValue) => {
      if (action === 'price') {
        // Update price for the specific post
        const updateList = (prev) => prev.map(post => {
          if ((post._id || post.id) === postId) {
            return { ...post, price: newValue, real_price: newValue };
          }
          return post;
        });

        if (listType === TAB_TYPE.FEED) {
          setFeedLists(updateList);
        } else if (listType === TAB_TYPE.MEDIA) {
          setMediaLists(updateList);
        } else if (listType === TAB_TYPE.FREE) {
          setFreeContentLists(updateList);
        }

        // Update in all tabs
        setFeedLists(prev => prev.map(p => (p._id || p.id) === postId ? { ...p, price: newValue, real_price: newValue } : p));
        setMediaLists(prev => prev.map(p => (p._id || p.id) === postId ? { ...p, price: newValue, real_price: newValue } : p));
        setFreeContentLists(prev => prev.map(p => (p._id || p.id) === postId ? { ...p, price: newValue, real_price: newValue } : p));
      } else if (action === 'caption') {
        // Update caption for the specific post
        const updateList = (prev) => prev.map(post => {
          if ((post._id || post.id) === postId) {
            return { ...post, caption: newValue.caption, tag_users: newValue.tag_users || [] };
          }
          return post;
        });

        if (listType === TAB_TYPE.FEED) {
          setFeedLists(updateList);
        } else if (listType === TAB_TYPE.MEDIA) {
          setMediaLists(updateList);
        } else if (listType === TAB_TYPE.FREE) {
          setFreeContentLists(updateList);
        }

        // Update in all tabs
        setFeedLists(prev => prev.map(p => (p._id || p.id) === postId ? { ...p, caption: newValue.caption, tag_users: newValue.tag_users || [] } : p));
        setMediaLists(prev => prev.map(p => (p._id || p.id) === postId ? { ...p, caption: newValue.caption, tag_users: newValue.tag_users || [] } : p));
        setFreeContentLists(prev => prev.map(p => (p._id || p.id) === postId ? { ...p, caption: newValue.caption, tag_users: newValue.tag_users || [] } : p));
      } else if (action === 'lock') {
        // Content was locked - move from free to media tab
        // Update in feed tab (feed shows all content)
        setFeedLists(prev => prev.map(p => {
          if ((p._id || p.id) === postId) {
            return { ...p, lock_content: true, price: newValue || p.price || p.real_price || 0, real_price: newValue || p.real_price || p.price || 0 };
          }
          return p;
        }));

        // Remove from free tab and add to media tab
        setFreeContentLists(prev => {
          const postIndex = prev.findIndex(p => (p._id || p.id) === postId);
          if (postIndex !== -1) {
            const post = prev[postIndex];
            const updatedPost = { ...post, lock_content: true, price: newValue || post.price || post.real_price || 0, real_price: newValue || post.real_price || post.price || 0 };
            // Add to media tab (React 18 batches this)
            setMediaLists(currentMedia => {
              const existsInMedia = currentMedia.find(p => (p._id || p.id) === postId);
              if (existsInMedia) {
                return currentMedia.map(p => (p._id || p.id) === postId ? updatedPost : p);
              }
              return [updatedPost, ...currentMedia];
            });
            // Remove from free tab
            return prev.filter((_, index) => index !== postIndex);
          }
          return prev;
        });

        // Also update in media if it already exists there
        setMediaLists(prev => {
          const postIndex = prev.findIndex(p => (p._id || p.id) === postId);
          if (postIndex !== -1) {
            return prev.map(p => (p._id || p.id) === postId ? { ...p, lock_content: true, price: newValue || p.price || p.real_price || 0, real_price: newValue || p.real_price || p.price || 0 } : p);
          }
          return prev;
        });

        // Refresh content counts
        if (userId) {
          fetchContentCountsData(userId);
        }
      } else if (action === 'unlock') {
        // Content was unlocked - move from media to free tab
        // Update in feed tab (feed shows all content)
        setFeedLists(prev => prev.map(p => {
          if ((p._id || p.id) === postId) {
            return { ...p, lock_content: false, price: 0, real_price: 0 };
          }
          return p;
        }));

        // Remove from media tab and add to free tab
        setMediaLists(prev => {
          const postIndex = prev.findIndex(p => (p._id || p.id) === postId);
          if (postIndex !== -1) {
            const post = prev[postIndex];
            const updatedPost = { ...post, lock_content: false, price: 0, real_price: 0 };
            // Add to free tab (React 18 batches this)
            setFreeContentLists(currentFree => {
              const existsInFree = currentFree.find(p => (p._id || p.id) === postId);
              if (existsInFree) {
                return currentFree.map(p => (p._id || p.id) === postId ? updatedPost : p);
              }
              return [updatedPost, ...currentFree];
            });
            // Remove from media tab
            return prev.filter((_, index) => index !== postIndex);
          }
          return prev;
        });

        // Also update in free if it already exists there
        setFreeContentLists(prev => {
          const postIndex = prev.findIndex(p => (p._id || p.id) === postId);
          if (postIndex !== -1) {
            return prev.map(p => (p._id || p.id) === postId ? { ...p, lock_content: false, price: 0, real_price: 0 } : p);
          }
          return prev;
        });

        // Refresh content counts
        if (userId) {
          fetchContentCountsData(userId);
        }
      } else if (action === 'pin' || action === 'unpin') {
        // Refresh content for pin/unpin to maintain proper order
        refreshContent();
      }
    };
  }, [refreshContent, userId, fetchContentCountsData]);

  const handleFeedPostUpdated = useMemo(() => createPostUpdateHandler(TAB_TYPE.FEED), [createPostUpdateHandler]);
  const handleMediaPostUpdated = useMemo(() => createPostUpdateHandler(TAB_TYPE.MEDIA), [createPostUpdateHandler]);
  const handleFreePostUpdated = useMemo(() => createPostUpdateHandler(TAB_TYPE.FREE), [createPostUpdateHandler]);


  return (
    <div className="profile-page-creator modern-creator-profile">
      <ModernHeader
        dflex="flex"
        hide={false}
        showAvatar={false}
        showBackButton={false}
      />

      <main className="profile-main-content">
        <UserProfileHeader
          isLogging={isLoggedIn}
          userRole={userRole}
          isOwnProfile={userId === creator?.user_data?._id}
          creatorLoading={creatorLoading && !hasCreatorData}
          creatorInfo={creator?.user_data || {}}
        />

        {/* Content Tabs */}
        <div className="tabs_section_min_height">
          <section className="content-tabs-section">
            <div className="content-tabs-container">
              <button
                className={`content-tab ${activeTab === TAB_TYPE.FEED ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(TAB_TYPE.FEED);
                  handleSetType(TAB_TYPE.FEED);
                }}
              >
                <div className="tab-icon-grid">
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                  <span className="grid-dot"></span>
                </div>
                <div className="tab-content">
                  <span>Feed</span>
                  {contentCounts.feedCount > 0 && (
                    <span className="tab-count-badge">
                      {contentCounts.feedCount}
                    </span>
                  )}
                </div>
              </button>

              <button
                className={`content-tab ${activeTab === TAB_TYPE.MEDIA ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(TAB_TYPE.MEDIA);
                  handleSetType(TAB_TYPE.MEDIA);
                }}
              >
                <div className="tab-icon">
                  <i className="fa-solid fa-photo-film tab-icon-fa"></i>
                </div>
                <div className="tab-content">
                  <span>Media</span>
                  {contentCounts.mediaCount > 0 && (
                    <span className="tab-count-badge">
                      {contentCounts.mediaCount}
                    </span>
                  )}
                </div>
              </button>

              <button
                className={`content-tab ${activeTab === TAB_TYPE.FREE ? "active" : ""}`}
                onClick={() => {
                  setActiveTab(TAB_TYPE.FREE);
                  handleSetType(TAB_TYPE.FREE);
                }}
              >
                <div className="tab-icon">
                  <i className="fa-solid fa-gift tab-icon-fa"></i>
                </div>
                <div className="tab-content">
                  <span>Free</span>
                  {contentCounts.freeContentCount > 0 && (
                    <span className="tab-count-badge">
                      {contentCounts.freeContentCount}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </section>
        </div>

        {/* Content Feed */}
        <section className="creator-feed-section">
          <TabContent activeTab={activeTab}>
            <TabPane className="text-center" tabId={TAB_TYPE.FEED} id="follows">
              {loading && !tabState.feed.hasInitiallyLoaded ? (
                <div className="creator-feed-loading">
                  <Loader type="Oval" color="#F334BE" height={60} width={60} />
                </div>
              ) : !loading && tabState.feed.hasInitiallyLoaded && (!feedLists || feedLists.length === 0) ? (
                <NoContent
                  message="Nothing here yet!"
                />
              ) : (
                feedLists && feedLists.map((post, index) => (
                  <FeedPost
                    key={post._id || index}
                    postId={post._id || index}
                    post={post}
                    isLogging={isLoggedIn}
                    userRole={userRole}
                    isCreatorOwnProfile={userId === creator?.user_data?._id}
                    isPinAlreadyExists={isPinAlreadyExists}
                    creatorInfo={creator?.user_data || {}}
                    openDropdownId={openDropdownId}
                    onDropdownToggle={handleDropdownToggle}
                    onPostDeleted={handleFeedPostDeleted}
                    onPostUpdated={handleFeedPostUpdated}
                    isLock={false}
                    isShowDuration={true}
                    showControls={true}
                  />
                ))
              )}
            </TabPane>
            <TabPane className="text-center" tabId={TAB_TYPE.MEDIA} id="following">
              {loading && !tabState.media.hasInitiallyLoaded ? (
                <div className="creator-feed-loading">
                  <Loader type="Oval" color="#F334BE" height={60} width={60} />
                </div>
              ) : !loading && tabState.media.hasInitiallyLoaded && (!mediaLists || mediaLists.length === 0) ? (
                <NoContent
                  message="Nothing here yet!"
                />
              ) : (
                <section id="creator-feed" className="creator-feed-grid">
                  <div className="creator-feed-grid-container">
                    {mediaLists && mediaLists.map((post, index) => (
                      <MediaGrid
                        key={post._id || index}
                        tabType={TAB_TYPE.MEDIA}
                        postId={post._id || index}
                        index={index}
                        post={post}
                        isLogging={isLoggedIn}
                        userRole={userRole}
                        userId={userId}
                        isCreatorOwnProfile={userId === creator?.user_data?._id}
                        creatorInfo={creator?.user_data || {}}
                        openDropdownId={openDropdownId}
                        onDropdownToggle={handleDropdownToggle}
                        onPostDeleted={handleMediaPostDeleted}
                        onPostUpdated={handleMediaPostUpdated}
                        isLock={false}
                        isShowDuration={false}
                        showControls={false}
                        hideSliderDots={true}
                        posts={mediaLists}
                        currentIndex={index}
                      />
                    ))}
                  </div>
                </section>
              )}
            </TabPane>
            <TabPane className="text-center" tabId={TAB_TYPE.FREE} id="freeContent">
              {loading && !tabState.free.hasInitiallyLoaded ? (
                <div className="creator-feed-loading">
                  <Loader type="Oval" color="#F334BE" height={60} width={60} />
                </div>
              ) : !loading && tabState.free.hasInitiallyLoaded && (!freeContentLists || freeContentLists.length === 0) ? (
                <NoContent
                  message="Nothing here yet!"
                />
              ) : (
                <section id="creator-feed" className="creator-feed-grid">
                  <div className="creator-feed-grid-container">
                    {freeContentLists && freeContentLists.map((post, index) => (
                      <MediaGrid
                        key={post._id || index}
                        tabType={TAB_TYPE.FREE}
                        postId={post._id || index}
                        post={post}
                        index={index}
                        isLogging={isLoggedIn}
                        userRole={userRole}
                        userId={userId}
                        isCreatorOwnProfile={userId === creator?.user_data?._id}
                        creatorInfo={creator?.user_data || {}}
                        openDropdownId={openDropdownId}
                        onDropdownToggle={handleDropdownToggle}
                        onPostDeleted={handleFreePostDeleted}
                        onPostUpdated={handleFreePostUpdated}
                        isLock={false}
                        isShowDuration={false}
                        showControls={false}
                        hideSliderDots={true}
                        posts={freeContentLists}
                        currentIndex={index}
                      />
                    ))}
                  </div>
                </section>
              )}
            </TabPane>
          </TabContent>

          {/* Sentinel element for Intersection Observer - triggers load more when visible */}
          {tabState[type]?.hasInitiallyLoaded && tabState[type]?.hasMoreData && !loadingMore && (
            <div ref={loadMoreSentinelRef} style={{ height: '1px', width: '100%' }} />
          )}

          {loadingMore && tabState[type] && tabState[type].hasMoreData && (
            <div className="loading-more">
              <div className="loading-more-spinner">
                <Loader type="Oval" color="#F334BE" height={40} width={40} />
              </div>
            </div>
          )}
        </section>
      </main>

      <ModernFootermenu
        onUploadSuccess={handleNewContentUpload}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
    creator: state.creator?.creator,
    creatorLoading: state.creator?.loading || false,
  };
};

export default connect(mapStateToProps)(ProfilePageCreator);
